/**
 * Draft-editor counsel modes: Ask, Draft, Review — classification + routing helpers.
 */

export type EditorCounselMode = 'ask' | 'draft' | 'review';

export type ClassifierConfidence = 'high' | 'low';

export interface ClassifyPromptIntentResult {
  suggested: EditorCounselMode;
  confidence: ClassifierConfidence;
  signalMatched: string | null;
}

const REVIEW_SIGNALS: readonly string[] = [
  'review',
  'check',
  'proofread',
  'filing readiness',
  'missing',
  'completeness',
  'consistency',
  'numbering',
  'annexure',
  'prayer',
  'verify',
  'ready to file',
  'ready for filing',
  'sufficient',
  'survive',
  'defective',
  'objections',
  'anything wrong',
  'look over',
  'sign off',
  'good to go',
  'final check',
];

const DRAFT_SIGNALS: readonly string[] = [
  'draft',
  'rewrite',
  'redraft',
  'insert',
  'improve',
  'revise',
  'fix numbering',
  'add a',
  'add the',
  'write',
  'create',
  'generate',
  'compose',
  'prepare',
  'continue',
  'expand',
  'shorten',
  'update',
];

const DRAFT_VERBS_WITH_SELECTION = /\b(improve|rewrite|redraft|revise|fix|update|expand|shorten)\b/i;

function normalizeForMatch(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function matchSignal(text: string, signals: readonly string[]): string | null {
  const q = normalizeForMatch(text);
  if (!q) return null;
  const sorted = [...signals].sort((a, b) => b.length - a.length);
  for (const signal of sorted) {
    if (signal.includes(' ')) {
      if (q.includes(signal)) return signal;
      continue;
    }
    const re = new RegExp(`\\b${escapeRegex(signal)}\\b`, 'i');
    if (re.test(q)) return signal;
  }
  return null;
}

function prefersReviewOverDraft(text: string): boolean {
  const q = normalizeForMatch(text);
  return /^(review|check|proofread|verify|is this ready|are there any|any citation|any missing|look over|final check|good to go)/.test(
    q,
  );
}

function isAskQuestion(text: string): boolean {
  const q = normalizeForMatch(text);
  if (!q) return false;
  if (q.endsWith('?')) return true;
  return /^(what|why|how|when|where|who|is|are|can|should|does|do|explain|tell me|summarize|describe)\b/.test(
    q,
  );
}

export function classifyPromptIntent(
  text: string,
  hasSelection: boolean,
): ClassifyPromptIntentResult {
  const q = normalizeForMatch(text);
  if (!q) {
    return { suggested: 'ask', confidence: 'low', signalMatched: null };
  }

  const draftSignal = matchSignal(q, DRAFT_SIGNALS);
  const reviewSignal = matchSignal(q, REVIEW_SIGNALS);

  if (reviewSignal && draftSignal) {
    if (prefersReviewOverDraft(text)) {
      return { suggested: 'review', confidence: 'high', signalMatched: reviewSignal };
    }
    return { suggested: 'draft', confidence: 'high', signalMatched: draftSignal };
  }

  if (draftSignal) {
    return { suggested: 'draft', confidence: 'high', signalMatched: draftSignal };
  }

  if (hasSelection && DRAFT_VERBS_WITH_SELECTION.test(q)) {
    return { suggested: 'draft', confidence: 'high', signalMatched: 'selection+edit_verb' };
  }

  if (reviewSignal) {
    return { suggested: 'review', confidence: 'high', signalMatched: reviewSignal };
  }

  if (isAskQuestion(text)) {
    return { suggested: 'ask', confidence: 'high', signalMatched: 'question_pattern' };
  }

  return { suggested: 'ask', confidence: 'low', signalMatched: null };
}

export interface ResolveEditorCounselModeResult {
  mode: EditorCounselMode;
  modeBeforeSend: EditorCounselMode;
  autoSwitched: boolean;
  modeSwitchNotice: string | null;
  classification: ClassifyPromptIntentResult;
}

export function resolveEditorCounselMode(params: {
  selectedMode: EditorCounselMode;
  question: string;
  hasSelection: boolean;
}): ResolveEditorCounselModeResult {
  const { selectedMode, question, hasSelection } = params;
  const classification = classifyPromptIntent(question, hasSelection);
  const modeBeforeSend = selectedMode;

  if (classification.confidence === 'high' && classification.suggested !== selectedMode) {
    const label =
      classification.suggested === 'review'
        ? 'Review'
        : classification.suggested === 'draft'
          ? 'Draft'
          : 'Ask';
    return {
      mode: classification.suggested,
      modeBeforeSend,
      autoSwitched: true,
      modeSwitchNotice: `Switched to ${label} for this question`,
      classification,
    };
  }

  return {
    mode: selectedMode,
    modeBeforeSend,
    autoSwitched: false,
    modeSwitchNotice: null,
    classification,
  };
}

export function editorCounselModeLabel(mode: EditorCounselMode): string {
  switch (mode) {
    case 'draft':
      return 'Draft';
    case 'review':
      return 'Review';
    default:
      return 'Ask';
  }
}

/** Tracks prior auto-switch for telemetry on the next send. */
export interface LexCounselModeTelemetryState {
  priorAutoSwitch: {
    modeBeforeSend: EditorCounselMode;
    modeAfterSend: EditorCounselMode;
  } | null;
}

let telemetryState: LexCounselModeTelemetryState = { priorAutoSwitch: null };

export function recordAutoSwitchForTelemetry(
  modeBeforeSend: EditorCounselMode,
  modeAfterSend: EditorCounselMode,
): void {
  telemetryState = {
    priorAutoSwitch: { modeBeforeSend, modeAfterSend },
  };
}

export function consumeUserSwitchedBackTelemetry(
  currentSelectedMode: EditorCounselMode,
): boolean {
  const prior = telemetryState.priorAutoSwitch;
  if (!prior) return false;
  telemetryState = { priorAutoSwitch: null };
  return currentSelectedMode === prior.modeBeforeSend && prior.modeAfterSend !== prior.modeBeforeSend;
}

export function clearModeTelemetryState(): void {
  telemetryState = { priorAutoSwitch: null };
}
